/*
 * This file is part of glowingblue/orell-articles.
 *
 * Copyright (c) Glowing Blue AG.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import type { ComponentAttrs } from 'flarum/common/Component';
import { components } from '@glowingblue-orell-thalia-common';
import type { SwiperInstance, SwiperOptions } from '@glowingblue-orell-thalia-common/components/AbstractSlider';

import ArticleSliderItem from './ArticleSliderItem';
import type Article from '../models/Article';

const { AbstractSlider } = components;

export interface ArticleSliderAttrs extends ComponentAttrs {
	articles?: Article[];
	selectedBookId?: string;
}

export default class ArticleSlider extends AbstractSlider<Article, ArticleSliderAttrs> {
	get sliderItemComponent() {
		return ArticleSliderItem;
	}

	protected getItems(attrs: ArticleSliderAttrs) {
		return attrs.articles ?? [];
	}

	/**
	 * Only the keys that genuinely differ from the base's defaults: one article per view, no
	 * looping and no autoplay. Everything else — including the base's decision to hide
	 * navigation, pagination and autoplay once there is a single slide — is left to the baseline,
	 * so improvements made there keep reaching this slider instead of being shadowed by a
	 * restated copy.
	 */
	protected swiperOptionOverrides(): Partial<SwiperOptions> {
		return {
			slidesPerView: 1,
			loop: false,
			autoplay: false,
		};
	}

	onSwiperCreated(swiper: SwiperInstance) {
		this.setInitialSlide(swiper);
	}

	/**
	 * Jump to the article the page was opened for, without animating.
	 */
	setInitialSlide(swiper: SwiperInstance) {
		const { selectedBookId } = this.attrs;

		if (!selectedBookId) {
			return;
		}

		const initialSlideIndex = this.items.findIndex((article) => article.id() === selectedBookId);

		if (initialSlideIndex >= 0) {
			swiper.slideTo(initialSlideIndex, 0);
		}
	}
}
